import { useState, type FormEvent } from 'react';
import { Landmark, CheckCircle2 } from 'lucide-react';
import { patchMyTutorProfile } from '@/lib/tutor-api';
import { Field, FooterButtons, type StepProps, withCompletedStep } from './shared';

export function BankStep({ profile, accessToken, onSaved, onError }: StepProps) {
  const onFile = profile?.bankDetailsOnFile === true;
  const [keepExisting, setKeepExisting] = useState(onFile);
  const [accountHolderName, setAccountHolderName] = useState('');
  const [bankName, setBankName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [ifscOrSwift, setIfscOrSwift] = useState('');
  const [country, setCountry] = useState('India');
  const [busy, setBusy] = useState(false);

  const skip = async () => {
    setBusy(true);
    try {
      const next = await patchMyTutorProfile(accessToken, {
        completedSteps: withCompletedStep(profile, 'bank'),
      });
      onSaved(next);
    } catch (err) {
      onError(err);
    } finally {
      setBusy(false);
    }
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();

    // Keeping existing details → mark the step complete and advance.
    if (keepExisting && onFile) {
      setBusy(true);
      try {
        const next = await patchMyTutorProfile(accessToken, {
          completedSteps: withCompletedStep(profile, 'bank'),
        });
        onSaved(next);
      } catch (err) {
        onError(err);
      } finally {
        setBusy(false);
      }
      return;
    }

    if (
      accountHolderName.trim() === '' ||
      bankName.trim() === '' ||
      accountNumber.trim() === '' ||
      ifscOrSwift.trim() === '' ||
      country.trim() === ''
    ) {
      onError(new Error('Please fill in all bank detail fields.'));
      return;
    }
    setBusy(true);
    try {
      const next = await patchMyTutorProfile(accessToken, {
        bankDetails: { accountHolderName, bankName, accountNumber, ifscOrSwift, country },
        completedSteps: withCompletedStep(profile, 'bank'),
      });
      onSaved(next);
    } catch (err) {
      onError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="space-y-5" onSubmit={submit}>
      <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-muted-foreground dark:border-slate-700 dark:bg-slate-800/60">
        🔒 Used for payouts only. Encrypted at rest — neither the admin team nor any other
        tutor can read these values once saved.
      </div>

      {onFile ? (
        <>
          {/* Confirmation card */}
          <div className="flex items-start gap-4 rounded-xl border border-emerald-200 bg-emerald-50/60 p-4 dark:border-emerald-800/40 dark:bg-slate-800/60">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-900/30">
              <Landmark className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <div className="flex items-center gap-1.5">
                <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-400">
                  Bank details already on file
                </p>
              </div>
              <p className="mt-1 text-xs text-emerald-600 dark:text-emerald-500">
                Your payout details are securely saved. You can continue or update them below.
              </p>
            </div>
          </div>

          {/* Choice: keep or replace */}
          <div className="space-y-2">
            <p className="text-sm font-medium text-foreground">What would you like to do?</p>
            <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-slate-200 px-4 py-3 hover:bg-slate-50 dark:border-slate-600 dark:hover:bg-slate-700/40">
              <input
                type="radio"
                name="bankChoice"
                checked={keepExisting}
                onChange={() => setKeepExisting(true)}
                className="h-4 w-4 text-brand-600 focus:ring-brand-600"
              />
              <div>
                <p className="text-sm font-medium text-foreground">Keep my current details</p>
                <p className="text-xs text-muted-foreground">Continue with the bank details already on file</p>
              </div>
            </label>
            <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-slate-200 px-4 py-3 hover:bg-slate-50 dark:border-slate-600 dark:hover:bg-slate-700/40">
              <input
                type="radio"
                name="bankChoice"
                checked={!keepExisting}
                onChange={() => setKeepExisting(false)}
                className="h-4 w-4 text-brand-600 focus:ring-brand-600"
              />
              <div>
                <p className="text-sm font-medium text-foreground">Update bank details</p>
                <p className="text-xs text-muted-foreground">Replace the saved details with new ones</p>
              </div>
            </label>
          </div>
        </>
      ) : (
        <div className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/60">
          <Landmark className="mt-0.5 h-5 w-5 shrink-0 text-brand-600" />
          <div className="text-sm text-muted-foreground space-y-1">
            <p>
              Add your bank account details for payouts. These are encrypted at rest and only
              used to transfer your earnings.
            </p>
            <p className="font-medium text-amber-600 dark:text-amber-400">
              Note: This step is optional. You can skip this and fill these details later from your profile settings.
            </p>
          </div>
        </div>
      )}

      {/* Form fields — shown when no details on file, or tutor chose to update */}
      {(!onFile || !keepExisting) && (
        <>
          <Field id="bank-holder" label="Account holder name" value={accountHolderName} onChange={setAccountHolderName} placeholder="Full name as on your bank account" />
          <Field id="bank-name" label="Bank name" value={bankName} onChange={setBankName} placeholder="e.g. State Bank of India" />
          <Field id="bank-account" label="Account number" value={accountNumber} onChange={setAccountNumber} autoComplete="off" placeholder="Your account number" />
          <Field id="bank-ifsc" label="IFSC / SWIFT code" value={ifscOrSwift} onChange={setIfscOrSwift} placeholder="e.g. SBIN0001234" />
          <Field id="bank-country" label="Country" value={country} onChange={setCountry} placeholder="Country where the bank is based" />
        </>
      )}

      <FooterButtons
        primaryLabel="Save & continue"
        busy={busy}
        secondaryLabel="Skip for now"
        onSecondaryClick={skip}
        secondaryVariant="link"
      />
    </form>
  );
}
